import React, { useState } from "react";
import axios from "axios";
import { X, Phone, Briefcase, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "adeshwar_customer_token_v1";

export function GoogleProfileDialog() {
  const { pendingUser, setPendingUser, profileOpen, setProfileOpen } = useAuth();
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (!profileOpen || !pendingUser) return null;
  const needsProfile = !pendingUser.phone || !pendingUser.business_name;

  const submit = async () => {
    setErr("");
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await axios.post(
        `${API}/auth/complete-profile`,
        { phone, business_name: business },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      setPendingUser(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" data-testid="google-profile-dialog">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-[#0056D2] px-6 py-5 text-white relative">
          <button data-testid="google-profile-close" onClick={() => setProfileOpen(false)} className="absolute top-3 right-3 p-1 rounded hover:bg-white/20">
            <X size={18} />
          </button>
          <h3 className="font-display text-xl font-black">
            {needsProfile ? "Almost there!" : "Awaiting approval"}
          </h3>
          <p className="text-sm text-blue-100 mt-1">
            {needsProfile
              ? `Welcome ${pendingUser.name}! Tell us about your business so Adeshwar Electricals can approve your trade account.`
              : "Adeshwar Electricals is reviewing your account. Trade prices unlock once approved."}
          </p>
        </div>
        <div className="p-6">
          {needsProfile ? (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">Mobile number (WhatsApp)</label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="google-profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" className="w-full h-11 pl-10 pr-3 rounded-lg border border-gray-300 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-sm" />
              </div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 mt-3">Business name</label>
              <div className="relative">
                <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="google-profile-business" value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="e.g. Sri Ganesh Electricals" className="w-full h-11 pl-10 pr-3 rounded-lg border border-gray-300 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-sm" />
              </div>
              {err && <p className="text-sm text-[#EF4444] mt-2 font-semibold">{err}</p>}
              <button
                data-testid="google-profile-submit"
                disabled={loading || phone.replace(/\D/g, "").length !== 10 || !business.trim()}
                onClick={submit}
                className="mt-4 w-full h-11 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm disabled:opacity-50"
              >
                {loading ? "Saving..." : "Submit for Approval"}
              </button>
            </>
          ) : (
            <div className="text-center py-4" data-testid="google-profile-pending">
              <div className="w-14 h-14 rounded-full bg-amber-100 grid place-items-center mx-auto mb-3">
                <Clock size={26} className="text-amber-600" />
              </div>
              <p className="text-sm text-gray-700 font-semibold">Your details are with Adeshwar Electricals.</p>
              <p className="text-xs text-gray-500 mt-1">You'll see trade prices as soon as your account is approved.</p>
              <button data-testid="google-profile-ok" onClick={() => setProfileOpen(false)} className="mt-4 w-full h-10 rounded-lg bg-[#0056D2] text-white font-bold text-sm">
                Got it
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
